import React, { FC } from "react";
import Pagination from "shared/Pagination/Pagination";

interface Column {
  key: string;
  label: string;
  render?: (item: any) => React.ReactNode;
}

interface DynamicTableProps {
  columns: Column[];
  data: any[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  onPageChange: (pageNumber: number) => void;
  onAction?: (action: string, item: any) => void;
  availableActions?: string[]; // New prop to specify which actions are available
  className?: string;
}

const DynamicTable: FC<DynamicTableProps> = ({
  columns,
  data,
  totalItems,
  currentPage,
  totalPages,
  onPageChange,
  onAction,
  availableActions = ["view", "edit", "delete"], // Default to all actions if not specified
  className = "",
}) => {
  return (
    <div className={`overflow-hidden bg-white rounded-lg shadow-lg dark:bg-gray-800 ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm text-gray-700 dark:text-gray-300">
          <thead className="text-xs tracking-wide text-gray-600 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-200">
            <tr>
              {columns?.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`px-6 py-4 ${column.key === "actions" ? "text-center" : "text-left"}`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((item, index) => (
              <tr
                key={index}
                className="transition-colors border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {columns?.map((column) => (
                  <td
                    key={column.key}
                    className={`px-6 py-4 ${column.key === "actions" ? "text-center" : "text-left"} ${
                      column.key !== "actions" ? "font-medium" : ""
                    }`}
                  >
                    {column?.key === "actions" && onAction ? (
                      <div className="flex justify-center space-x-4">
                        {availableActions.includes("view") && (
                          <button
                            onClick={() => onAction("view", item)}
                            className="text-blue-500 transition-colors hover:text-blue-700"
                            aria-label={`View ${item.designation || "item"}`}
                          >
                            <i className="text-xl las la-eye"></i>
                          </button>
                        )}
                        {availableActions.includes("edit") && (
                          <button
                            onClick={() => onAction("edit", item)}
                            className="text-green-500 transition-colors hover:text-green-700"
                            aria-label={`Edit ${item.designation || "item"}`}
                          >
                            <i className="text-xl las la-pen-alt"></i>
                          </button>
                        )}
                        {availableActions.includes("delete") && (
                          <button
                            onClick={() => onAction("delete", item)}
                            className="text-red-500 transition-colors hover:text-red-700"
                            aria-label={`Delete ${item.designation || "item"}`}
                          >
                            <i className="text-xl las la-trash-alt"></i>
                          </button>
                        )}
                      </div>
                    ) : column.render ? (
                      column.render(item)
                    ) : (
                      item[column.key] || "-"
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-gray-800">
        <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded-full dark:bg-blue-900 dark:text-blue-200">
          {totalItems} éléments
        </span>
        {typeof totalPages === "number" && totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            className="flex space-x-2"
          />
        )}
      </div>
    </div>
  );
};

export default DynamicTable;
